import { AxiosError } from 'axios';
import { Input, PendingGrid } from '@finbeat/react-toolkit';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { ClientOrder } from 'models/api/client';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Container, List } from 'reactstrap';
import { useClientWorkplaceState } from '../Clients';
import OrderInfoModal from '../OrderInfoModal';
import './OrdersTab.scss';

type ClientChangeModalOrdersProps = {
    clientId: number | string;
    isShown: boolean;
    query: () => Promise<ClientOrder[]>;
};

const OrdersTab = ({ clientId, isShown, query }: ClientChangeModalOrdersProps) => {
    useTranslation();
    const { handleError } = useClientWorkplaceState();
    const [queryEnable, setQueryEnable] = useState(isShown);
    useEffect(() => {
        if (isShown) {
            setQueryEnable(true);
        }
    }, [isShown]);
    const [orderId, setOrderId] = useState('');
    const { t } = useTranslation();
    const [loansIssued, setLoansIssued] = useState(true);

    return (
        <div>
            <Container className="my-3">
                <Input
                    checked={loansIssued}
                    id="loans-issued"
                    label={t('BackOfficeLoansIssued')}
                    type="checkbox"
                    onChange={(e) => setLoansIssued(e.target.checked)}
                />
            </Container>
            <PendingGrid<ClientOrder, AxiosError<AxiosErrorResponse>>
                storagePath="client-orders-tab-grid"
                columns={[
                    {
                        key: 'id',
                        name: <Trans i18nKey="BackOfficeAppId" />,
                        formatter: ({ row }) => (
                            <List className="client-orders__list">
                                <li>
                                    <Link
                                        to={`/order/${row.id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOrderId(row.id.toString());
                                        }}
                                    >
                                        {row.id}
                                    </Link>
                                </li>
                                <li>
                                    <DataTimeText value={row.dt} />
                                </li>
                            </List>
                        ),
                        width: 100,
                    },
                    {
                        key: 'product',
                        name: <Trans i18nKey="BackOfficeOrderSettingBasketType" />,
                        width: 100,
                    },
                    {
                        key: 'productSum',
                        name: <Trans i18nKey="BackOfficeAmount" />,
                        formatter: ({ row }) => <Money value={row.productSum} />,
                        width: 100,
                    },
                    { key: 'status', name: <Trans i18nKey="BackOfficeStatus" />, width: 150 },
                    {
                        key: 'requestedAmount',
                        name: <Trans i18nKey="BackOfficeRequestedAmount" />,
                        formatter: ({ row }) => (
                            <List className="client-orders__list">
                                <li>
                                    <Money value={row.requestedAmount} />
                                </li>
                                <li>
                                    <DataTimeText value={row.statusDt} />
                                </li>
                            </List>
                        ),
                    },
                    {
                        key: 'totalDaysUsage',
                        name: <Trans i18nKey="BackOfficeLoanTotalDaysUsage" />,
                    },
                    {
                        key: 'daysOverdueGet',
                        name: <Trans i18nKey="BackOfficeLoanDaysOverdue" />,
                    },
                ]}
                query={query}
                handleError={(error: AxiosError<AxiosErrorResponse>) => {
                    handleError(error.response?.data);
                }}
                filter={loansIssued ? (d) => d.filter((i) => i.hasLoan) : undefined}
                queryKey={'clientOrders' + clientId}
                className="client-orders"
                lineHeight={60}
                queryEnable={queryEnable}
            />
            {orderId !== '' && (
                <OrderInfoModal id={orderId} toggle={() => setOrderId('')} isOpen={true} />
            )}
        </div>
    );
};

export default OrdersTab;
